import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { Breadcrumbs, VaBadgeModule, VaBreadcrumbsModule } from '@vendasta/uikit';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { OneTimeService } from '../one-time/one-time.service';
import {
  DynamicOpenCloseTemplateRef,
  DynamicOpenCloseTemplateRefService,
} from '../side-drawer/dynamic-open-close-template-ref.service';
import { HeaderButton, NavigationDepth } from './header-button';
import { SideDrawerContainerComponent } from './side-drawer-container/side-drawer-container.component';

@Component({
  imports: [
    AsyncPipe,
    InfiniteScrollModule,
    VaBreadcrumbsModule,
    VaBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    RouterLink,
    NgClass,
    GalaxyTooltipModule,
    NgTemplateOutlet,
    MatMenuModule,
    CdkOverlayOrigin,
    GalaxyPopoverModule,
    NgIf,
    NgForOf,
    SideDrawerContainerComponent,
  ],
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
})
export class PageComponent implements OnInit, OnDestroy {
  @ViewChild('mainPageContainer', { static: true })
  mainPageContainer: ElementRef;
  @Input() pageTitle: string;
  @Input() pageSideHelpId: string;
  @Input() pageSubtitle: string;
  @Input() pageSubtitleLinkText: string;
  @Input() pageSubtitleLink: string;

  private _buttons: HeaderButton[];
  primaryButtons: HeaderButton[] = [];
  secondaryButtons: HeaderButton[] = [];
  tertiaryButtons: HeaderButton[] = [];
  helpOpened$: Observable<boolean>;

  PopoverPositions = PopoverPositions;
  showPopover$: Observable<boolean>;
  private subscriptions: Subscription[] = [];
  hidePopover = false;

  get buttons(): HeaderButton[] {
    return this._buttons;
  }

  @Input() set buttons(value: HeaderButton[]) {
    this._buttons = value;
    this.primaryButtons = value
      ? value.filter(
          (button) => button.navigationDepth === undefined || button.navigationDepth === NavigationDepth.primary,
        )
      : [];
    this.secondaryButtons = value
      ? value.filter(
          (button) => button.navigationDepth !== undefined && button.navigationDepth === NavigationDepth.secondary,
        )
      : [];
    this.tertiaryButtons = value
      ? value.filter(
          (button) => button.navigationDepth !== undefined && button.navigationDepth === NavigationDepth.tertiary,
        )
      : [];
  }

  @Input() breadcrumbs: Breadcrumbs[];
  @Input() badgeColor: string;
  @Input() badgeText: string;

  @Output() loadMore: EventEmitter<any> = new EventEmitter();
  @Output() scrolled = new EventEmitter();

  sideDrawerTemplate$: Observable<DynamicOpenCloseTemplateRef>;

  constructor(
    @Optional()
    private dynamicOpenCloseTemplateRefService: DynamicOpenCloseTemplateRefService,
    private oneTimePopoverService: OneTimeService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnInit(): void {
    if (this.dynamicOpenCloseTemplateRefService) {
      this.sideDrawerTemplate$ = this.dynamicOpenCloseTemplateRefService.openTemplate$;
      this.helpOpened$ = this.dynamicOpenCloseTemplateRefService.open$;
    }

    if (this.helpOpened$) {
      this.subscriptions.push(
        this.helpOpened$
          .pipe(
            tap((open) => {
              if (!open) {
                this.oneTimePopoverService.trigger(OneTimeService.HELP_POPOVER);
              }
            }),
          )
          .subscribe(),
      );
    }

    this.showPopover$ = this.oneTimePopoverService.show$(OneTimeService.HELP_POPOVER);
  }

  emitLoadMore(): void {
    this.loadMore.emit();
  }

  scrollToTop(): void {
    this.mainPageContainer.nativeElement.scrollTop = 0;
  }

  clicked(button: HeaderButton): void {
    if (!button.callback) {
      console.log(`clicked on ${button.title} but it has no callback`);
      return;
    }
    button.callback();
  }

  public showSubtitleLink(): boolean {
    return !!this.pageSubtitleLink && !!this.pageSubtitleLinkText;
  }

  onWindowScroll(): void {
    this.scrolled.emit();
  }

  pageHelpToggle(): void {
    this.dynamicOpenCloseTemplateRefService.toggle(this.pageSideHelpId);
  }

  hide(): void {
    this.oneTimePopoverService.markAsShown(OneTimeService.HELP_POPOVER);
  }

  showTooltipOnDisabledButton(button: HeaderButton): boolean {
    return button.disabled && button.disabledTooltip !== '';
  }
}

// TODO: Move to "navigation" folder
